import { Fragment, useMemo } from 'react';

import {
  FilterAppliedProps,
  FilterComparer,
  FilterConfig,
  FilterMenuProps,
  Filters,
  useCurrentFilter,
} from '@components/filters';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconLanguage } from '@components/icons/language';
import { CareerSiteLanguageType } from '@locale/locale-store';

import { useStore } from 'models/Provider';
import { ITODO } from 'types/todo';

import { FilterActionButtons } from './filter-action-buttons';
import { FilterComparerMenu } from './filter-comparer-menu';

const LANGUAGE_COMPARERS: Array<FilterComparer> = ['is', 'not_is', 'any_of', 'none_of'];

type ExtraProps = {
  langs: Array<CareerSiteLanguageType>;
};

function FilterApplicationLanguageApplied({ data }: FilterAppliedProps<ExtraProps>) {
  const { t } = useStore();

  const valueLabel = Array.isArray(data.value)
    ? data.value.map((language) => t(`general.${language}`))
    : t(`general.${data.value as string}`);

  return (
    <Filters.AppliedFilter
      icon={<IconLanguage />}
      category={t('applicants.filters.language.label')}
      value={valueLabel}
      comparer={{
        value: data.comparer!,
        label: t('comparers.' + data.comparer).replace('...', ''),
      }}
    />
  );
}

function FilterApplicationLanguageMenu({ langs }: FilterMenuProps<ExtraProps>) {
  const { t } = useStore();
  const { state, actions, isMultiComparer, hasValue } = useCurrentFilter();

  const filterValueItems = langs.map((item) => ({
    value: item,
    label: t('general.' + item),
  }));

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('applicants.filters.language.label')}
        icon={<IconArrowLeft />}
        onClick={actions.unselectCategory}
      />

      {state.comparer && (
        <Filters.ListItem
          heading
          label={t('comparers.' + state.comparer).replace('...', '')}
          icon={<IconArrowLeft />}
          onClick={actions.unselectComparer}
        />
      )}

      <Filters.ListSeparator />

      {!state.comparer && (
        <Fragment>
          {filterValueItems.map((item) => (
            <Filters.ListItem
              key={item.value}
              label={item.label}
              onClick={() => {
                actions.applyFilter({ comparer: 'is', value: item.value });
              }}
            />
          ))}
          <Filters.ListSeparator />
          <FilterComparerMenu
            comparers={LANGUAGE_COMPARERS}
            onComparerSelect={actions.selectComparer}
          />
        </Fragment>
      )}

      {state.comparer && (
        <Fragment>
          <Filters.FilterValueMenu
            items={filterValueItems}
            isMulti={isMultiComparer}
            onSetValue={actions.selectValue}
            state={state}
          />
          <FilterActionButtons disableApply={!hasValue} />
        </Fragment>
      )}
    </Filters.List>
  );
}

export function useFilterApplicationLanguage(job?: ITODO): FilterConfig<ExtraProps> | null {
  const { t, current } = useStore();

  const langs: Array<CareerSiteLanguageType> = job ? job.langIds : current.info.langs;

  return useMemo(() => {
    if (langs.length <= 1) return null;
    return {
      category: 'language',
      label: t('applicants.filters.language.label'),
      icon: <IconLanguage />,
      AppliedFilter: FilterApplicationLanguageApplied,
      Menu: FilterApplicationLanguageMenu,
      extraProps: {
        langs,
      },
      toQueryData: (data) => {
        switch (data.comparer) {
          case 'is':
          case 'any_of':
            return { language: data.value };
          case 'not_is':
          case 'none_of':
            return { language__exclude: data.value };
          default:
            return {};
        }
      },
    };
  }, [langs, t]);
}
